import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MainContainer } from '../MainContainer'
import AdminFinanceNav from '../../components/AdminFinanceNav'
import {
  createMoneyPayout,
  createMoneyTestOrder,
  deleteMoneyPayout,
  deleteMoneyTestOrders,
  fetchMoneyBalances,
  fetchMoneyLedger,
  fetchMoneyOrders,
  fetchMoneyOverview,
  fetchMoneyPayouts,
  fetchMoneySettings,
  getMoneyErrorMessage,
  recalcMoneyOrder,
  saveMoneyOrderCost,
  saveMoneySettings,
} from '../../api/adminMoneyApi'
import '../../scss/components/_adminMoneyPage.scss'

/**
 * «Деньги»: сколько пришло, сколько должны по заказам, как делится
 * заработок и кому сколько уже выплачено. Балансы и заказы страница
 * не меняет: выплаты и фактические затраты — отдельные учётные записи.
 */
const TABS = [
  { value: 'total', text: 'Итог' },
  { value: 'payouts', text: 'Остатки и выплаты' },
  { value: 'income', text: 'Приход' },
  { value: 'orders', text: 'Заказы' },
  { value: 'settings', text: 'Настройки' },
]

const PERIODS = [
  { key: 'today', label: 'Сегодня' },
  { key: 'week', label: '7 дней' },
  { key: 'month', label: 'Месяц' },
  { key: 'all', label: 'Всё время' },
]

// Вкладки, которые зависят от выбранного периода.
const PERIOD_TABS = ['total', 'income', 'orders']

const STATUS_LABELS = {
  pending: 'ждёт оплаты',
  completed: 'оплачен',
  cancelled: 'отменён',
  refund: 'возврат',
}

const periodOf = (key) => {
  if (key === 'all') return {}
  const from = new Date()
  from.setHours(0, 0, 0, 0)
  if (key === 'week') from.setDate(from.getDate() - 6)
  // «Месяц» — с первого числа текущего месяца.
  if (key === 'month') from.setDate(1)
  return { from: from.toISOString() }
}

const money = (value) => `${Number(value || 0).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ₽`
const dateTime = (value) => (value ? new Date(value).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' }) : '—')
const toAmount = (value) => Number(String(value ?? '').replace(/\s/g, '').replace(',', '.'))
const todayInput = () => {
  const now = new Date()
  const pad = (part) => String(part).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

const Metric = ({ label, value, sub, tone }) => (
  <article className={`admin-money__metric ${tone ? `is-${tone}` : ''}`}>
    <span>{label}</span>
    <strong>{value}</strong>
    {sub ? <small>{sub}</small> : null}
  </article>
)

const Row = ({ label, value, tone, strong }) => (
  <div className={`admin-money__row ${strong ? 'is-strong' : ''}`}>
    <span>{label}</span>
    <b className={tone ? `is-${tone}` : ''}>{value}</b>
  </div>
)

// Части раскладки: порядок и цвета одинаковые в полосе и в подписи.
const SPLIT_PARTS = [
  { key: 'costs', label: 'Затраты' },
  { key: 'partner', label: 'Напарнику' },
  { key: 'site', label: 'Сайту' },
  { key: 'cofounder', label: 'Соучредителю' },
  { key: 'tax', label: 'Налог' },
]

/** Цветная полоса: из каких частей состоит сумма. Отрицательные части не рисуются. */
const SplitBar = ({ split }) => {
  const parts = SPLIT_PARTS
    .map((part) => ({ ...part, value: Math.max(0, Number(split?.[part.key]) || 0) }))
    .filter((part) => part.value > 0)
  const total = parts.reduce((sum, part) => sum + part.value, 0)
  if (!total) return null

  return (
    <div className="admin-money__splitbar">
      <div className="admin-money__splitbar-track" role="img" aria-label="Как делится сумма">
        {parts.map((part) => (
          <i key={part.key} className={`is-${part.key}`} style={{ width: `${(part.value * 100) / total}%` }} title={`${part.label}: ${money(part.value)}`} />
        ))}
      </div>
      <div className="admin-money__splitbar-legend">
        {parts.map((part) => (
          <span key={part.key}>
            <i className={`is-${part.key}`} />
            {part.label} {Math.round((part.value * 100) / total)}%
          </span>
        ))}
      </div>
    </div>
  )
}

/** Полоска «выплачено из начисленного». */
const Progress = ({ paid, accrued }) => {
  const share = accrued > 0 ? Math.min(100, Math.max(0, (paid * 100) / accrued)) : 0
  return (
    <div className="admin-money__progress" title={`Выплачено ${Math.round(share)}%`}>
      <i style={{ width: `${share}%` }} />
    </div>
  )
}

/** Раскладка суммы: затраты, напарнику, сайту, соучредителю, налог. */
const SplitRows = ({ split, policy }) => (
  <>
    <Row label="Затраты (заготовка с доставкой)" value={money(split.costs)} />
    <Row label="Прибыль напарника" value={money(split.partner)} tone={split.partner < 0 ? 'danger' : ''} />
    <Row label={`Доля сайта ${policy?.sitePercent ?? 6}%`} value={money(split.site)} tone="success" />
    {split.cofounder ? <Row label="Соучредителю офиса" value={money(split.cofounder)} /> : null}
    <Row label={`Отложить на налог ${policy?.taxPercent ?? 10}%`} value={money(split.tax)} tone="warning" />
  </>
)

export default function AdminMoneyPage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('total')
  const [periodKey, setPeriodKey] = useState('month')

  const [overview, setOverview] = useState(null)
  const [ledger, setLedger] = useState(null)
  const [orders, setOrders] = useState(null)
  const [balances, setBalances] = useState(null)
  const [payouts, setPayouts] = useState([])
  const [settings, setSettings] = useState(null)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [saving, setSaving] = useState(false)

  const [openOrderId, setOpenOrderId] = useState('')
  const [costDraft, setCostDraft] = useState('')

  const [payoutForm, setPayoutForm] = useState({ recipient: 'owner', amount: '', paidAt: todayInput(), comment: '' })
  const [sharesDraft, setSharesDraft] = useState(null)
  const [testForm, setTestForm] = useState({ productAmount: '15801.63', workAmount: '', officeId: '', paid: true })

  // Номер запроса: ответ на старую вкладку или период не затирает новый.
  const requestSeq = useRef(0)

  const canEdit = !!settings?.canEdit
  const policy = overview?.policy || settings?.policy

  const load = useCallback(async (tab, key) => {
    const seq = ++requestSeq.current
    const period = periodOf(key)
    setLoading(true)
    setError('')

    try {
      // Настройки нужны везде: права на правку и проценты.
      const settingsRequest = fetchMoneySettings()

      if (tab === 'total') {
        // Остатки нужны и на «Итоге»: сколько осталось выплатить и отложить на налог.
        const [data, rest, view] = await Promise.all([fetchMoneyOverview(period), fetchMoneyBalances(), settingsRequest])
        if (seq !== requestSeq.current) return
        setOverview(data)
        setBalances(rest)
        setSettings(view)
      } else if (tab === 'payouts') {
        const [rest, list, view] = await Promise.all([fetchMoneyBalances(), fetchMoneyPayouts(), settingsRequest])
        if (seq !== requestSeq.current) return
        setBalances(rest)
        setPayouts(list)
        setSettings(view)
      } else if (tab === 'income') {
        const [data, view] = await Promise.all([fetchMoneyLedger(period), settingsRequest])
        if (seq !== requestSeq.current) return
        setLedger(data)
        setSettings(view)
      } else if (tab === 'orders') {
        const [data, view] = await Promise.all([fetchMoneyOrders(period), settingsRequest])
        if (seq !== requestSeq.current) return
        setOrders(data)
        setSettings(view)
      } else {
        const view = await settingsRequest
        if (seq !== requestSeq.current) return
        setSettings(view)
        setSharesDraft(null)
      }
    } catch (loadError) {
      if (seq === requestSeq.current) setError(getMoneyErrorMessage(loadError))
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    load(activeTab, periodKey)
  }, [load, activeTab, periodKey])

  const refreshCurrent = useCallback(() => load(activeTab, periodKey), [load, activeTab, periodKey])

  /** «Показать ещё»: следующая порция ленты или заказов дописывается в конец. */
  const loadMore = useCallback(async () => {
    if (loading) return
    const seq = ++requestSeq.current
    const period = periodOf(periodKey)
    setLoading(true)
    setError('')

    try {
      if (activeTab === 'income' && ledger?.nextBefore) {
        const next = await fetchMoneyLedger(period, ledger.nextBefore)
        if (seq !== requestSeq.current) return
        const known = new Set(ledger.items.map((item) => item.id))
        setLedger({ ...next, items: [...ledger.items, ...next.items.filter((item) => !known.has(item.id))] })
      } else if (activeTab === 'orders' && orders?.hasMore) {
        const next = await fetchMoneyOrders(period, (orders.page || 1) + 1)
        if (seq !== requestSeq.current) return
        const known = new Set(orders.orders.map((order) => order.id))
        setOrders({ ...next, orders: [...orders.orders, ...next.orders.filter((order) => !known.has(order.id))] })
      }
    } catch (loadError) {
      if (seq === requestSeq.current) setError(getMoneyErrorMessage(loadError))
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }, [loading, periodKey, activeTab, ledger, orders])

  /** Общая обёртка для сохранений: блокировка кнопок, ошибка, перечитывание. */
  const runAction = useCallback(async (action, successText = '') => {
    if (saving) return false
    setSaving(true)
    setError('')
    setNotice('')
    try {
      await action()
      if (successText) setNotice(successText)
      await load(activeTab, periodKey)
      return true
    } catch (actionError) {
      setError(getMoneyErrorMessage(actionError, 'Не удалось сохранить'))
      return false
    } finally {
      setSaving(false)
    }
  }, [saving, load, activeTab, periodKey])

  const submitPayout = async (event) => {
    event.preventDefault()
    const amount = toAmount(payoutForm.amount)
    if (!(amount > 0)) {
      setError('Укажите сумму выплаты')
      return
    }
    const done = await runAction(() => createMoneyPayout({
      recipient: payoutForm.recipient,
      amount,
      // Полдень — чтобы дата не съехала из-за часового пояса.
      paidAt: payoutForm.paidAt ? new Date(`${payoutForm.paidAt}T12:00:00`).toISOString() : undefined,
      comment: payoutForm.comment,
    }), 'Выплата записана')
    if (done) setPayoutForm((value) => ({ ...value, amount: '', comment: '' }))
  }

  const removePayout = (payout) => {
    if (!window.confirm(`Удалить выплату ${money(payout.amount)} (${payout.recipientName})?`)) return
    runAction(() => deleteMoneyPayout(payout.id), 'Выплата удалена')
  }

  const toggleOrder = (order) => {
    if (openOrderId === order.id) {
      setOpenOrderId('')
      return
    }
    setOpenOrderId(order.id)
    setCostDraft(order.costsActual ? String(order.split.costs) : '')
  }

  const saveCost = (order, reset = false) => {
    const costs = reset ? null : toAmount(costDraft)
    if (!reset && !(costs >= 0)) {
      setError('Укажите затраты числом')
      return
    }
    runAction(() => saveMoneyOrderCost(order.id, costs), reset ? 'Затраты снова расчётные' : 'Затраты сохранены')
  }

  const recalcOrder = (order) => {
    if (!window.confirm(`Пересчитать заказ № ${order.orderNumber} по сегодняшним процентам и режиму «пополам»?`)) return
    runAction(() => recalcMoneyOrder(order.id), 'Заказ пересчитан по текущим настройкам')
  }

  const toggleOffice = (officeId, value) => runAction(() => saveMoneySettings({
    halfOfficeIds: settings.offices.filter((office) => (office.id === officeId ? value : office.half)).map((office) => office.id),
  }))

  const toggleViewer = (adminId, value) => runAction(() => saveMoneySettings({
    viewerUserIds: settings.admins
      .filter((admin) => admin.role !== 'superAdmin')
      .filter((admin) => (admin.id === adminId ? value : admin.canView))
      .map((admin) => admin.id),
  }))

  const shares = sharesDraft || settings?.digitalShares || []
  const sharesSum = shares.reduce((sum, share) => sum + (toAmount(share.percent) || 0), 0)

  const changeShare = (key, patch) => {
    setSharesDraft(shares.map((share) => (share.key === key ? { ...share, ...patch } : share)))
  }

  const saveShares = () => runAction(() => saveMoneySettings({
    digitalShares: shares.map((share) => ({ key: share.key, name: share.name, percent: toAmount(share.percent) || 0 })),
  }), 'Доли сохранены')

  const createTest = (event) => {
    event.preventDefault()
    const productAmount = toAmount(testForm.productAmount) || 0
    const workAmount = toAmount(testForm.workAmount) || 0
    if (productAmount <= 0 && workAmount <= 0) {
      setError('Укажите сумму товара или работ')
      return
    }
    runAction(
      () => createMoneyTestOrder({ productAmount, workAmount, officeId: testForm.officeId, paid: testForm.paid }),
      'Тестовый заказ создан — смотрите «Заказы» и «Итог»',
    )
  }

  const removeTests = () => {
    if (!window.confirm('Удалить все тестовые заказы (номер «ТЕСТ-…»)?')) return
    runAction(async () => {
      const deleted = await deleteMoneyTestOrders()
      setNotice(`Удалено тестовых заказов: ${deleted}`)
    })
  }

  const recipientOptions = useMemo(() => {
    if (balances?.recipients?.length) return balances.recipients.map((item) => ({ key: item.key, name: item.name }))
    return [
      { key: 'owner', name: 'Владелец сайта' },
      { key: 'partner', name: 'Напарник' },
      { key: 'service', name: 'Обслуживание и расходы' },
      { key: 'cofounder', name: 'Соучредитель офиса' },
      { key: 'tax', name: 'Налог' },
      { key: 'costs', name: 'Затраты (закупка)' },
    ]
  }, [balances])

  const renderTotal = () => {
    if (!overview) return null
    const { income, digital, orders: sum } = overview

    // Остатки — за всё время, от периода не зависят.
    const restOf = (key) => Number((balances?.recipients || []).find((item) => item.key === key)?.rest) || 0
    const toPay = ['owner', 'partner', 'service', 'cofounder'].reduce((total, key) => total + Math.max(0, restOf(key)), 0)
    const taxRest = Math.max(0, restOf('tax'))

    return (
      <>
        <div className="admin-money__metrics">
          <Metric label="Пришло наличными за заказы" value={money(income.cash)} sub="С учётом записанных возвратов" tone="success" />
          <Metric label="Пришло картой через банк" value={money(income.card)} sub={`вручную пополнено: ${money(income.manual)}`} tone="success" />
          <Metric label={`Заказы за период: ${sum.count}`} value={money(sum.total)} sub={`оплачено ${money(sum.paid)} · цифровое ${money(digital.total)}`} />
          <Metric label="Должны по заказам" value={money(sum.debt)} sub="покупатели ещё не оплатили" tone={sum.debt > 0 ? 'danger' : 'success'} />
          <Metric label="Осталось выплатить участникам" value={money(toPay)} sub="за всё время, с оплаченного" tone={toPay > 0 ? 'warning' : 'success'} />
          <Metric label="Отложить на налог" value={money(taxRest)} sub="за всё время, ещё не уплачено" tone={taxRest > 0 ? 'warning' : 'success'} />
        </div>

        <div className="admin-money__grid">
          <section className="admin-money__panel">
            <header><h3>Пришло денег</h3></header>
            <Row label="Картой через банк" value={money(income.card)} tone="success" strong />
            <Row label="· пополнения баланса" value={money(income.cardTopups)} />
            <Row label="· оплата заказов" value={money(income.cardOrders)} />
            <Row label="· 3D-модели" value={money(income.cardDigital)} />
            <Row label="Наличные за заказы" value={money(income.cash)} tone="success" strong />
            <Row label="Переводы за заказы" value={money(income.bankTransfer)} />
            <Row label="Картой вне сайта" value={money(income.cardOutsideSite)} />
            <Row label="Пополнено вручную (не из банка)" value={money(income.manual)} />
          </section>

          <section className="admin-money__panel">
            <header><h3>Цифровое</h3></header>
            <Row label="Всего" value={money(digital.total)} strong />
            {digital.kinds.map((kind) => (
              <Row key={kind.label} label={`· ${kind.label} (${kind.count})`} value={money(kind.amount)} />
            ))}
            <div className="admin-money__divider" />
            {digital.shares.map((share) => (
              <Row key={share.key} label={`${share.name}, ${share.percent}%`} value={money(share.amount)} tone="success" />
            ))}
            <p className="admin-money__hint">
              Считается то, что потратили с баланса или оплатили картой. Покупки в счёт кредитного лимита тоже здесь,
              хотя деньги по ним ещё не пришли.
            </p>
          </section>

          <section className="admin-money__panel">
            <header><h3>Начислено со всех заказов</h3></header>
            <SplitBar split={sum.earned} />
            <SplitRows split={sum.earned} policy={policy} />
          </section>

          <section className="admin-money__panel">
            <header><h3>Получено с оплаченной части</h3></header>
            <SplitBar split={sum.received} />
            <SplitRows split={sum.received} policy={policy} />
          </section>
        </div>

        <p className="admin-money__hint">
          Затраты расчётные (по наценке из настроек цен: офис {policy?.markupSubscriber ?? 50}%, сайт {policy?.markupPublic ?? 100}%),
          пока по заказу не внесены фактические — это делается на вкладке «Заказы».
          {sum.cancelled ? ` Отменённых и возвращённых заказов: ${sum.cancelled}, в счёт не идут.` : ''}
          {sum.tests ? ` Тестовых заказов: ${sum.tests}, в счёт не идут.` : ''}
          {' '}«Заказы» и «Начислено» относятся к дате создания, «Получено» — к датам оплат и ручных возвратов.
          Старые ручные суммы без отдельной проводки включены в итоги за всё время; дата их получения неизвестна.
        </p>
      </>
    )
  }

  const renderPayouts = () => {
    if (!balances) return null

    return (
      <>
        <section className="admin-money__panel">
          <header><h3>Кому сколько осталось отдать (за всё время)</h3></header>
          <div className="admin-money__table-wrap">
            <table className="admin-money__table">
              <thead>
                <tr>
                  <th>Кому</th>
                  <th>С заказов</th>
                  <th>С цифрового</th>
                  <th>Начислено</th>
                  <th>Выплачено</th>
                  <th aria-label="Доля выплаченного" />
                  <th>Осталось</th>
                </tr>
              </thead>
              <tbody>
                {balances.recipients.map((item) => (
                  <tr key={item.key}>
                    <td>{item.name}</td>
                    <td>{money(item.fromOrders)}</td>
                    <td>{money(item.fromDigital)}</td>
                    <td><b>{money(item.accrued)}</b></td>
                    <td>{money(item.paid)}</td>
                    <td><Progress paid={item.paid} accrued={item.accrued} /></td>
                    <td><b className={item.rest > 0 ? 'is-warning' : item.rest < 0 ? 'is-danger' : 'is-success'}>{money(item.rest)}</b></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="admin-money__hint">
            «Начислено» считается только с оплаченной части заказов и с цифрового. «Осталось» со знаком минус —
            выплатили больше, чем начислено. Тестовые заказы не учитываются.
          </p>
        </section>

        {canEdit ? (
          <section className="admin-money__panel">
            <header><h3>Записать выплату</h3></header>
            <form className="admin-money__form" onSubmit={submitPayout}>
              <label>
                <span>Кому</span>
                <select
                  value={payoutForm.recipient}
                  onChange={(event) => setPayoutForm((value) => ({ ...value, recipient: event.target.value }))}
                >
                  {recipientOptions.map((item) => <option key={item.key} value={item.key}>{item.name}</option>)}
                </select>
              </label>
              <label>
                <span>Сумма, ₽</span>
                <input
                  inputMode="decimal"
                  value={payoutForm.amount}
                  onChange={(event) => setPayoutForm((value) => ({ ...value, amount: event.target.value }))}
                  placeholder="0"
                />
              </label>
              <label>
                <span>Дата</span>
                <input
                  type="date"
                  value={payoutForm.paidAt}
                  onChange={(event) => setPayoutForm((value) => ({ ...value, paidAt: event.target.value }))}
                />
              </label>
              <label className="is-wide">
                <span>Комментарий</span>
                <input
                  value={payoutForm.comment}
                  maxLength={500}
                  onChange={(event) => setPayoutForm((value) => ({ ...value, comment: event.target.value }))}
                  placeholder="Например: перевод на карту, налог за квартал"
                />
              </label>
              <button type="submit" className="admin-money__button" disabled={saving}>Записать</button>
            </form>
            <p className="admin-money__hint">Запись только для учёта: сами деньги и балансы она не двигает.</p>
          </section>
        ) : null}

        <section className="admin-money__panel">
          <header><h3>История выплат</h3></header>
          {payouts.length ? (
            <div className="admin-money__table-wrap">
              <table className="admin-money__table">
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>Кому</th>
                    <th>Сумма</th>
                    <th>Комментарий</th>
                    <th>Записал</th>
                    {canEdit ? <th aria-label="Действия" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((payout) => (
                    <tr key={payout.id}>
                      <td>{payout.paidAt ? new Date(payout.paidAt).toLocaleDateString('ru-RU') : '—'}</td>
                      <td>{payout.recipientName}</td>
                      <td><b>{money(payout.amount)}</b></td>
                      <td>{payout.comment || '—'}</td>
                      <td>{payout.createdByName || '—'}</td>
                      {canEdit ? (
                        <td>
                          <button type="button" className="admin-money__link is-danger" disabled={saving} onClick={() => removePayout(payout)}>
                            Удалить
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="admin-money__empty">Выплат пока нет</div>}
        </section>
      </>
    )
  }

  const renderIncome = () => {
    if (!ledger) return null
    if (!ledger.items.length) return <div className="admin-money__empty">За этот период операций нет</div>

    return (
      <section className="admin-money__panel">
        <div className="admin-money__table-wrap">
          <table className="admin-money__table">
            <thead>
              <tr>
                <th>Когда</th>
                <th>Операция</th>
                <th>Чей баланс</th>
                <th>Кто оплатил</th>
                <th>Описание</th>
                <th>Сумма</th>
              </tr>
            </thead>
            <tbody>
              {ledger.items.map((item) => (
                <tr key={item.id}>
                  <td>{dateTime(item.at)}</td>
                  <td>{item.title}</td>
                  <td>{item.targetName || '—'}</td>
                  <td>{item.payerName || '—'}</td>
                  <td>{item.description || '—'}</td>
                  <td>
                    <b className={item.direction === 'in' ? 'is-success' : ''}>
                      {item.direction === 'in' ? '+' : '−'}{money(item.amount)}
                    </b>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="admin-money__hint">
          «+» — деньги пришли (пополнение, оплата картой). «−» — офис или человек потратил с баланса (подписка, пакет, сцена).
        </p>
        {ledger.hasMore ? (
          <button type="button" className="admin-money__button is-ghost" disabled={loading} onClick={loadMore}>Показать ещё</button>
        ) : null}
      </section>
    )
  }

  const renderOrders = () => {
    if (!orders) return null
    if (!orders.orders.length) return <div className="admin-money__empty">За этот период заказов нет</div>

    return (
      <section className="admin-money__panel">
        <div className="admin-money__table-wrap">
          <table className="admin-money__table">
            <thead>
              <tr>
                <th>Заказ</th>
                <th>Через что</th>
                <th>Статус</th>
                <th>Сумма</th>
                <th>Оплачено</th>
                <th>Должны</th>
                <th>Затраты</th>
                <th>Напарнику</th>
                <th>Сайту</th>
                <th>Налог</th>
              </tr>
            </thead>
            <tbody>
              {orders.orders.map((order) => {
                const open = openOrderId === order.id
                return (
                  <React.Fragment key={order.id}>
                    <tr className={`is-clickable ${open ? 'is-open' : ''}`} onClick={() => toggleOrder(order)}>
                      <td>
                        <b>№ {order.orderNumber}</b>
                        <small>{dateTime(order.createdAt)}</small>
                      </td>
                      <td>
                        {order.viaOffice ? `Офис ${order.officeName}`.trim() : 'Сайт'}
                        {order.half ? <em className="admin-money__badge">пополам</em> : null}
                        {order.isTest ? <em className="admin-money__badge is-warning">тест</em> : null}
                      </td>
                      <td>{STATUS_LABELS[order.paymentStatus] || order.paymentStatus}</td>
                      <td><b>{money(order.total)}</b></td>
                      <td>{money(order.paid)}</td>
                      <td><b className={order.debt > 0 ? 'is-danger' : ''}>{money(order.debt)}</b></td>
                      <td>
                        {money(order.split.costs)}
                        <small>{order.costsActual ? 'фактические' : 'расчётные'}</small>
                      </td>
                      <td><span className={order.split.partner < 0 ? 'is-danger' : ''}>{money(order.split.partner)}</span></td>
                      <td>{money(order.split.site)}</td>
                      <td>{money(order.split.tax)}</td>
                    </tr>
                    {open ? (
                      <tr className="admin-money__details-row">
                        <td colSpan={10}>
                          <div className="admin-money__details">
                            <div>
                              <SplitBar split={order.split} />
                              <SplitRows split={order.split} policy={policy} />
                              <p className="admin-money__hint">
                                Заказ считается по своим зафиксированным настройкам: сайт {order.sitePercent}%, налог {order.taxPercent}%,
                                наценка {order.markupPercent}%{order.half ? ', режим «пополам»' : ''}. Расчётные затраты: {money(order.costsEstimated)}.
                              </p>
                            </div>
                            <div className="admin-money__details-actions">
                              {canEdit ? (
                                <div className="admin-money__form is-compact">
                                  <label>
                                    <span>Фактические затраты, ₽</span>
                                    <input
                                      inputMode="decimal"
                                      value={costDraft}
                                      onChange={(event) => setCostDraft(event.target.value)}
                                      placeholder={String(order.costsEstimated)}
                                    />
                                  </label>
                                  <button type="button" className="admin-money__button" disabled={saving} onClick={() => saveCost(order)}>
                                    Сохранить
                                  </button>
                                  {order.costsActual ? (
                                    <button type="button" className="admin-money__link" disabled={saving} onClick={() => saveCost(order, true)}>
                                      Вернуть расчётные
                                    </button>
                                  ) : null}
                                </div>
                              ) : null}
                              <button type="button" className="admin-money__link" onClick={() => navigate(`/orders?notificationOrderId=${order.id}`)}>
                                Открыть заказ
                              </button>
                              {canEdit ? (
                                <button type="button" className="admin-money__link" disabled={saving} onClick={() => recalcOrder(order)}>
                                  Пересчитать по текущим настройкам
                                </button>
                              ) : null}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
        <p className="admin-money__hint">
          Нажмите на заказ — покажет раскладку и поле для фактических затрат. Смена процентов и режима «пополам»
          действует на новые заказы; старый можно пересчитать кнопкой внутри заказа.
        </p>
        {orders.hasMore ? (
          <button type="button" className="admin-money__button is-ghost" disabled={loading} onClick={loadMore}>Показать ещё</button>
        ) : null}
      </section>
    )
  }

  const renderSettings = () => {
    if (!settings) return null

    return (
      <div className="admin-money__grid">
        <section className="admin-money__panel">
          <header><h3>Проценты (из настроек цен сайта)</h3></header>
          <Row label="Доля сайта" value={`${settings.policy.sitePercent}%`} />
          <Row label="Налог" value={`${settings.policy.taxPercent}%`} />
          <Row label="Наценка: офис с подпиской" value={`${settings.policy.markupSubscriber}%`} />
          <Row label="Наценка: продажа через сайт" value={`${settings.policy.markupPublic}%`} />
          <p className="admin-money__hint">Меняются в настройках цен, здесь только показаны.</p>
        </section>

        <section className="admin-money__panel">
          <header><h3>Цифровое делится так</h3></header>
          {shares.map((share) => (
            <div key={share.key} className="admin-money__share">
              <input
                value={share.name}
                maxLength={60}
                disabled={!canEdit}
                onChange={(event) => changeShare(share.key, { name: event.target.value })}
                aria-label="Название доли"
              />
              <input
                inputMode="decimal"
                value={share.percent}
                disabled={!canEdit}
                onChange={(event) => changeShare(share.key, { percent: event.target.value })}
                aria-label="Процент"
              />
              <span>%</span>
            </div>
          ))}
          <p className="admin-money__hint">
            Сумма: {Math.round(sharesSum * 100) / 100}%. Если не 100, доли пересчитаются пропорционально.
            Первая доля получает ещё долю сайта с заказов, вторая — прибыль производства.
          </p>
          {canEdit && sharesDraft ? (
            <button type="button" className="admin-money__button" disabled={saving} onClick={saveShares}>Сохранить доли</button>
          ) : null}
        </section>

        <section className="admin-money__panel">
          <header><h3>Офисы «пополам»</h3></header>
          {settings.offices.length ? settings.offices.map((office) => (
            <label key={office.id} className="admin-money__check">
              <input
                type="checkbox"
                checked={office.half}
                disabled={!canEdit || saving}
                onChange={(event) => toggleOffice(office.id, event.target.checked)}
              />
              <span>{office.name}</span>
            </label>
          )) : <div className="admin-money__empty">Офисов пока нет</div>}
          <p className="admin-money__hint">
            Для офиса, где вы с соучредителем начальники 50 на 50: прибыль и доля сайта с его заказов делятся надвое.
          </p>
        </section>

        <section className="admin-money__panel">
          <header><h3>Кто видит «Деньги»</h3></header>
          {settings.admins.map((admin) => (
            <label key={admin.id} className="admin-money__check">
              <input
                type="checkbox"
                checked={admin.canView}
                disabled={!canEdit || saving || admin.role === 'superAdmin'}
                onChange={(event) => toggleViewer(admin.id, event.target.checked)}
              />
              <span>{admin.name}{admin.role === 'superAdmin' ? ' (главный)' : ''}</span>
            </label>
          ))}
          <p className="admin-money__hint">Остальные админы видят раздел только для просмотра. Править может главный админ.</p>
        </section>

        {canEdit ? (
          <section className="admin-money__panel">
            <header><h3>Проверка: тестовый заказ</h3></header>
            <form className="admin-money__form" onSubmit={createTest}>
              <label>
                <span>Сумма товара, ₽</span>
                <input
                  inputMode="decimal"
                  value={testForm.productAmount}
                  onChange={(event) => setTestForm((value) => ({ ...value, productAmount: event.target.value }))}
                />
              </label>
              <label>
                <span>Сумма работ, ₽</span>
                <input
                  inputMode="decimal"
                  value={testForm.workAmount}
                  onChange={(event) => setTestForm((value) => ({ ...value, workAmount: event.target.value }))}
                />
              </label>
              <label>
                <span>Через что заказали</span>
                <select
                  value={testForm.officeId}
                  onChange={(event) => setTestForm((value) => ({ ...value, officeId: event.target.value }))}
                >
                  <option value="">Сайт</option>
                  {settings.offices.map((office) => (
                    <option key={office.id} value={office.id}>{office.name}{office.half ? ' · пополам' : ''}</option>
                  ))}
                </select>
              </label>
              <label className="admin-money__check">
                <input
                  type="checkbox"
                  checked={testForm.paid}
                  onChange={(event) => setTestForm((value) => ({ ...value, paid: event.target.checked }))}
                />
                <span>Заказ оплачен</span>
              </label>
              <button type="submit" className="admin-money__button" disabled={saving}>Создать тестовый заказ</button>
              <button type="button" className="admin-money__link is-danger" disabled={saving} onClick={removeTests}>
                Удалить тестовые заказы
              </button>
            </form>
            <p className="admin-money__hint">
              Тестовый заказ получает номер «ТЕСТ-…» и виден в списках заказов, пока не удалите. Денег и уведомлений он не создаёт.
            </p>
          </section>
        ) : null}
      </div>
    )
  }

  return (
    <MainContainer
      title="Деньги"
      fluid
      offAll
      buttonText="Обновить"
      handleShow={refreshCurrent}
      headerActions={<AdminFinanceNav />}
      className="admin-money-shell"
    >
      <div className="admin-money">
        <div className="admin-money__tabs" role="tablist" aria-label="Разделы учёта денег">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={tab.value === activeTab}
              className={tab.value === activeTab ? 'is-active' : ''}
              onClick={() => setActiveTab(tab.value)}
            >
              {tab.text}
            </button>
          ))}
        </div>

        {PERIOD_TABS.includes(activeTab) ? (
          <div className="admin-money__periods" role="group" aria-label="Период">
            {PERIODS.map((item) => (
              <button
                key={item.key}
                type="button"
                className={item.key === periodKey ? 'is-active' : ''}
                onClick={() => setPeriodKey(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>
        ) : null}

        {error ? <div className="admin-money__error">{error}</div> : null}
        {notice ? <div className="admin-money__notice">{notice}</div> : null}
        {loading ? <div className="admin-money__loading">Загружаем…</div> : null}

        {activeTab === 'total' ? renderTotal() : null}
        {activeTab === 'payouts' ? renderPayouts() : null}
        {activeTab === 'income' ? renderIncome() : null}
        {activeTab === 'orders' ? renderOrders() : null}
        {activeTab === 'settings' ? renderSettings() : null}
      </div>
    </MainContainer>
  )
}
